/**
 * Policy kind chips (Keep / Remove / SL / TP) — FE_POLICY_KIND_CHIPS.md
 */

import { isPolicyTreeV2 } from "../types/tradePolicy";
import { legAvailabilityFromPolicyRules } from "./exitPlacementSimLegs";

export const POLICY_KIND_ORDER = ["keep", "remove", "sl", "tp"] as const;

export type PolicyKind = (typeof POLICY_KIND_ORDER)[number];

export type PolicyEntryAction = "keep_only" | "veto";

export type PolicyExitLegs = "none" | "sl" | "tp" | "both";

const KIND_SET = new Set<string>(POLICY_KIND_ORDER);

export function isPolicyKind(value: unknown): value is PolicyKind {
  return typeof value === "string" && KIND_SET.has(value.trim().toLowerCase());
}

export function policyKindLabel(kind: PolicyKind): string {
  if (kind === "keep") return "Keep";
  if (kind === "remove") return "Remove";
  if (kind === "sl") return "SL";
  return "TP";
}

/** Stable order: keep → remove → sl → tp. */
export function orderPolicyKinds(kinds: Iterable<PolicyKind>): PolicyKind[] {
  const set = new Set(kinds);
  return POLICY_KIND_ORDER.filter((k) => set.has(k));
}

export function normalizePolicyKinds(raw: unknown): PolicyKind[] {
  if (!Array.isArray(raw)) return [];
  const out: PolicyKind[] = [];
  for (const item of raw) {
    if (typeof item !== "string") continue;
    const k = item.trim().toLowerCase();
    if (isPolicyKind(k) && !out.includes(k)) out.push(k);
  }
  return orderPolicyKinds(out);
}

export function normalizePolicyEntryActions(raw: unknown): PolicyEntryAction[] {
  if (!Array.isArray(raw)) return [];
  const out: PolicyEntryAction[] = [];
  for (const item of raw) {
    if (item === "keep_only" || item === "veto") {
      if (!out.includes(item)) out.push(item);
    }
  }
  return out;
}

export function normalizePolicyExitLegs(raw: unknown): PolicyExitLegs | null {
  if (raw === "none" || raw === "sl" || raw === "tp" || raw === "both") return raw;
  return null;
}

function kindsFromEntryActions(actions: PolicyEntryAction[]): PolicyKind[] {
  const out: PolicyKind[] = [];
  if (actions.includes("keep_only")) out.push("keep");
  if (actions.includes("veto")) out.push("remove");
  return out;
}

function kindsFromExitLegs(legs: PolicyExitLegs | null): PolicyKind[] {
  if (legs === "sl") return ["sl"];
  if (legs === "tp") return ["tp"];
  if (legs === "both") return ["sl", "tp"];
  return [];
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/** Classify from a full policy_tree when BE fields are missing (Builder / stale cache). */
export function classifyPolicyKindsFromTree(policyTree: unknown): PolicyKind[] {
  if (!isPlainObject(policyTree)) return [];

  const metaKinds = normalizePolicyKinds(
    policyTree.policy_kinds ??
      policyTree.policyKinds ??
      (isPlainObject(policyTree.meta)
        ? policyTree.meta.policy_kinds ?? policyTree.meta.policyKinds
        : undefined)
  );
  if (metaKinds.length > 0) return metaKinds;

  const metaExit = normalizePolicyExitLegs(
    policyTree.exit_legs ??
      policyTree.exitLegs ??
      (isPlainObject(policyTree.meta)
        ? policyTree.meta.exit_legs ?? policyTree.meta.exitLegs
        : undefined)
  );
  const metaEntry = normalizePolicyEntryActions(
    policyTree.entry_actions ??
      policyTree.entryActions ??
      (isPlainObject(policyTree.meta)
        ? policyTree.meta.entry_actions ?? policyTree.meta.entryActions
        : undefined)
  );
  const fromMetaExtras = orderPolicyKinds([
    ...kindsFromEntryActions(metaEntry),
    ...kindsFromExitLegs(metaExit),
  ]);
  if (fromMetaExtras.length > 0) return fromMetaExtras;

  if (isPolicyTreeV2(policyTree)) {
    const legs = legAvailabilityFromPolicyRules(policyTree.rules);
    return orderPolicyKinds([
      ...(legs.hasSl ? (["sl"] as const) : []),
      ...(legs.hasTp ? (["tp"] as const) : []),
    ]);
  }

  const segments = Array.isArray(policyTree.segments) ? policyTree.segments : null;
  if (segments && segments.length > 0) {
    const kinds = new Set<PolicyKind>();
    for (const seg of segments) {
      if (!isPlainObject(seg)) continue;
      const kind = String(seg.segment_kind ?? "").trim().toLowerCase();
      const action = seg.action;
      if (action === "keep_only") kinds.add("keep");
      if (action === "veto") kinds.add("remove");
      if (kind === "management" || Array.isArray(seg.rules) || isPlainObject(seg.rule)) {
        const rules: unknown[] = Array.isArray(seg.rules)
          ? seg.rules
          : isPlainObject(seg.rule)
            ? [seg.rule]
            : [];
        const legs = legAvailabilityFromPolicyRules(rules as never);
        if (legs.hasSl) kinds.add("sl");
        if (legs.hasTp) kinds.add("tp");
      }
    }
    return orderPolicyKinds(kinds);
  }

  const action = policyTree.action;
  if (action === "keep_only") return ["keep"];
  if (action === "veto") return ["remove"];
  return [];
}

export type ResolvePolicyKindsInput = {
  policyKinds?: unknown;
  entryActions?: unknown;
  exitLegs?: unknown;
  policyTree?: unknown;
};

/**
 * Prefer BE `policy_kinds`; else derive from entry_actions + exit_legs; else classify tree.
 */
export function resolvePolicyKinds(input: ResolvePolicyKindsInput): PolicyKind[] {
  const fromKinds = normalizePolicyKinds(input.policyKinds);
  if (fromKinds.length > 0) return fromKinds;

  const fromEntryExit = orderPolicyKinds([
    ...kindsFromEntryActions(normalizePolicyEntryActions(input.entryActions)),
    ...kindsFromExitLegs(normalizePolicyExitLegs(input.exitLegs)),
  ]);
  if (fromEntryExit.length > 0) return fromEntryExit;

  return classifyPolicyKindsFromTree(input.policyTree);
}

/** Read kinds from a policy version API row (snake or camel). */
export function resolvePolicyKindsFromVersion(version: {
  policy_kinds?: unknown;
  policyKinds?: unknown;
  entry_actions?: unknown;
  entryActions?: unknown;
  exit_legs?: unknown;
  exitLegs?: unknown;
  policy_tree?: unknown;
} | null | undefined): PolicyKind[] {
  if (!version) return [];
  return resolvePolicyKinds({
    policyKinds: version.policy_kinds ?? version.policyKinds,
    entryActions: version.entry_actions ?? version.entryActions,
    exitLegs: version.exit_legs ?? version.exitLegs,
    policyTree: version.policy_tree,
  });
}

/** Read kinds from workspace node meta (camel preferred). */
export function resolvePolicyKindsFromWorkspaceMeta(meta: {
  policyKinds?: unknown;
  policy_kinds?: unknown;
  entryActions?: unknown;
  entry_actions?: unknown;
  exitLegs?: unknown;
  exit_legs?: unknown;
} | null | undefined): PolicyKind[] {
  if (!meta) return [];
  return resolvePolicyKinds({
    policyKinds: meta.policyKinds ?? meta.policy_kinds,
    entryActions: meta.entryActions ?? meta.entry_actions,
    exitLegs: meta.exitLegs ?? meta.exit_legs,
  });
}
